import { DependencyList, useCallback, useEffect, useRef, useState } from 'react';

type AsyncState<T> = {
  data: Nullable<T>;
  error: unknown;
  isLoading: boolean;
};

/**
 * Loads data when the dependencies change. A response of an outdated request never overwrites a newer one.
 * `enabled = false` keeps the current data and skips loading.
 */
const useAsyncData = <T>(load: () => Promise<T>, deps: DependencyList, enabled = true) => {
  const [state, setState] = useState<AsyncState<T>>({ data: null, error: null, isLoading: enabled });
  const loadRef = useRef(load);
  const sequenceRef = useRef(0);

  loadRef.current = load;

  const reload = useCallback(async () => {
    sequenceRef.current += 1;
    const sequence = sequenceRef.current;

    setState(previous => ({ ...previous, error: null, isLoading: true }));

    try {
      const data = await loadRef.current();

      if (sequence === sequenceRef.current) {
        setState({ data, error: null, isLoading: false });
      }
    } catch (error) {
      if (sequence === sequenceRef.current) {
        setState(previous => ({ ...previous, error, isLoading: false }));
      }
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  useEffect(
    () => () => {
      // invalidates an in-flight request after unmount
      sequenceRef.current += 1;
    },
    [],
  );

  return { ...state, reload };
};

export default useAsyncData;
